const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Helper to get auth headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const productService = {
  async getSubCategoryLabels(category: string): Promise<string[]> {
    if (!category) return [];
    const response = await fetch(
      `${API_BASE_URL}/products/meta/subcategories?category=${encodeURIComponent(category)}`
    );
    if (!response.ok) return [];
    const data = await response.json();
    return data.success && Array.isArray(data.data) ? data.data : [];
  },

  // Get all products with filtering
  async getProducts(params: Record<string, string | number | undefined> = {}) {
    const clean: Record<string, string> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        clean[k] = String(v);
      }
    });
    const queryString = new URLSearchParams(clean).toString();
    const response = await fetch(`${API_BASE_URL}/products?${queryString}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch products');
    return response.json();
  },

  // Get single product by ID
  async getProductById(id: string) {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch product');
    return response.json();
  },

  // Get product by slug
  async getProductBySlug(slug: string) {
    const response = await fetch(`${API_BASE_URL}/products/slug/${slug}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch product');
    return response.json();
  },

  // Get featured products
  async getFeaturedProducts(limit: number = 8) {
    const response = await fetch(`${API_BASE_URL}/products/featured/list?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch featured products');
    return response.json();
  },

  // Search products
  async searchProducts(query: string, limit: number = 20) {
    const response = await fetch(`${API_BASE_URL}/products/search?q=${encodeURIComponent(query)}&limit=${limit}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to search products');
    return response.json();
  },

  // Get search suggestions
  async getSearchSuggestions(query: string, limit: number = 8) {
    const response = await fetch(`${API_BASE_URL}/products/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get suggestions');
    return response.json();
  },

  // Create product (admin)
  async createProduct(productData: any, images: File[]) {
    const formData = new FormData();
    formData.append('product', JSON.stringify(productData));
    
    if (images && images.length > 0) {
      images.forEach(image => {
        formData.append('images', image);
      });
    }

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) throw new Error('Failed to create product');
    return response.json();
  },

  // Update product (admin)
  async updateProduct(id: string, productData: any, images?: File[]) {
    const formData = new FormData();
    formData.append('product', JSON.stringify(productData));
    
    if (images && images.length > 0) {
      images.forEach(image => {
        formData.append('images', image);
      });
    }

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) throw new Error('Failed to update product');
    return response.json();
  },

  // Delete product (admin)
  async deleteProduct(id: string) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to delete product');
    return response.json();
  },
};
